import { ContentType, ProgressForLessons, ProgressForCourses } from '@atlassian/learning-components';

import { LearningProgressStatus } from 'src/services/http/progress';
import { sendTrackEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';

interface NavigationItem {
  sys: {
    id: string;
  };
  slug: string;
}

interface LessonProgressItem {
  lessonId: string;
  status: LearningProgressStatus;
  section: {
    sectionId: string;
    status: LearningProgressStatus;
  }[];
  [key: string]: string | LearningProgressStatus | { sectionId: string; status: LearningProgressStatus }[];
}

interface CourseProgressItem {
  courseId: string;
  status: LearningProgressStatus;
  [key: string]: string | LearningProgressStatus;
}

interface NavigationOptions {
  items: NavigationItem[];
  progress: ProgressForCourses | ProgressForLessons;
  itemIdField: string;
  contentSlug: string;
  contentType: ContentType.COURSE | ContentType.LEARNING_PATH;
  userId?: string;
  navigateToItem: (slug: string) => void;
}

interface GetNextItemOptions {
  items: NavigationItem[];
  progress: ProgressForCourses | ProgressForLessons;
  itemIdField: string;
  contentType: ContentType.COURSE | ContentType.LEARNING_PATH;
}

export const getNextItemSlug = ({ items, progress, itemIdField, contentType }: GetNextItemOptions): string => {
  if (!items?.length) {
    return '';
  }

  const hasNotStarted = !progress.overallStatus || progress.overallStatus === LearningProgressStatus.NOT_STARTED;

  const shouldNavigateToFirstItem = hasNotStarted || progress.overallStatus === LearningProgressStatus.COMPLETED;

  const firstItemSlug = getSlug(items[0]);

  if (shouldNavigateToFirstItem) {
    return firstItemSlug;
  }

  // For courses
  if (contentType === ContentType.COURSE) {
    const lessons = (progress as ProgressForLessons).lessons || [];

    for (const item of items) {
      const itemProgress = lessons.find((p) => (p as LessonProgressItem)[itemIdField] === item.sys.id);

      if (itemProgress?.status !== LearningProgressStatus.COMPLETED) {
        return getSlug(item);
      }
    }
  } else {
    // For learning paths
    const courses = (progress as ProgressForCourses).courses || [];
    const inProgressItem = items.find((item) =>
      courses.some(
        (p) =>
          (p as CourseProgressItem)[itemIdField] === item.sys.id && p.status === LearningProgressStatus.IN_PROGRESS,
      ),
    );

    if (inProgressItem) {
      return getSlug(inProgressItem);
    }

    const notStartedItem = items.find((item) =>
      courses.some(
        (p) =>
          (p as CourseProgressItem)[itemIdField] === item.sys.id && p.status === LearningProgressStatus.NOT_STARTED,
      ),
    );

    if (notStartedItem) {
      return getSlug(notStartedItem);
    }
  }

  // Fallback to first item
  return firstItemSlug;
};

export const navigateToNextItem = ({
  items,
  progress,
  itemIdField,
  contentSlug,
  contentType,
  userId,
  navigateToItem,
}: NavigationOptions): void => {
  if (!items?.length) {
    return;
  }

  const hasNotStarted = !progress.overallStatus || progress.overallStatus === LearningProgressStatus.NOT_STARTED;

  if (hasNotStarted) {
    sendTrackEvent(
      'learningApp',
      contentType === ContentType.COURSE ? 'course' : 'learningPath',
      'started',
      {
        [`${contentType === ContentType.COURSE ? 'course' : 'learningPath'}Slug`]: contentSlug,
      },
      userId,
    );
  }

  const nextItemSlug = getNextItemSlug({ items, progress, itemIdField, contentType });

  navigateToItem(nextItemSlug);
};
